import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { TenantBootstrapModel } from "@unified-lk/tenant-config"
import type { AuthSession } from "../../../../types/plans"
import { staffApi } from "../../../../api/staff"
import {
  isDiagnocatAnalysisReady,
  shouldPollDiagnocatUploadJob,
  shouldSyncDiagnocatPatient,
  type DiagnocatWorkflowSnapshot,
} from "./domain/diagnocatWorkflow"
import type { DiagnocatUploadJob } from "./domain/diagnocatTypes"

const POLL_MS = 20_000
const SYNC_MS = 30_000
const MAX_POLL_MS = 15 * 60_000

function jobStorageKey(misPatientId: string): string {
  return `lk-diagnocat-submit-job:${misPatientId}`
}

export function snapshotFromDiagnocat(data: {
  total_analyses: number
  studies?: Array<{ analyses?: unknown[] }>
}): DiagnocatWorkflowSnapshot {
  const fromStudies =
    data.studies?.reduce((sum, study) => sum + (study.analyses?.length ?? 0), 0) ?? 0
  const findingsCount = Math.max(data.total_analyses ?? 0, fromStudies)
  return {
    findingsCount,
    analysesCount: data.total_analyses ?? 0,
  }
}

type DiagnocatSeed = {
  total_analyses: number
  studies?: Array<{ analyses?: unknown[] }>
} | null | undefined

type Params = {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  misPatientId: string | null
  lkPatientId: number | null
  enabled: boolean
  diagnocatSeed?: DiagnocatSeed
  onAnalysisReady?: () => void
}

export function useDiagnocatCtSubmitWorkflow({
  bootstrap,
  session,
  misPatientId,
  lkPatientId,
  enabled,
  diagnocatSeed,
  onAnalysisReady,
}: Params) {
  const [activeJobId, setActiveJobId] = useState<string | null>(() => {
    if (!misPatientId || typeof sessionStorage === "undefined") return null
    return sessionStorage.getItem(jobStorageKey(misPatientId))
  })
  const [job, setJob] = useState<DiagnocatUploadJob | null>(null)
  const [snapshot, setSnapshot] = useState<DiagnocatWorkflowSnapshot>(() =>
    diagnocatSeed ? snapshotFromDiagnocat(diagnocatSeed) : { findingsCount: 0, analysesCount: 0 },
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isPolling, setIsPolling] = useState(false)
  const [pollStartedAt, setPollStartedAt] = useState<number | null>(() =>
    activeJobId ? Date.now() : null,
  )
  const analysisReadyNotified = useRef(false)
  const bootstrapSyncDone = useRef(false)

  const seedSnapshot = useMemo(
    () => (diagnocatSeed ? snapshotFromDiagnocat(diagnocatSeed) : null),
    [diagnocatSeed],
  )

  const mergedSnapshot = useMemo(
    () => ({
      findingsCount: Math.max(snapshot.findingsCount, seedSnapshot?.findingsCount ?? 0),
      analysesCount: Math.max(snapshot.analysesCount, seedSnapshot?.analysesCount ?? 0),
    }),
    [snapshot, seedSnapshot],
  )

  const analysisReady = isDiagnocatAnalysisReady(job, mergedSnapshot)
  const jobPipelineActive = Boolean(activeJobId) && !analysisReady
  const needsSnapshotSync = shouldSyncDiagnocatPatient(job, mergedSnapshot)

  const refreshSnapshot = useCallback(async () => {
    if (!lkPatientId) return
    try {
      const data = await staffApi.getPatientDiagnocat(bootstrap, session, lkPatientId)
      setSnapshot(snapshotFromDiagnocat(data))
    } catch {
      /* keep last snapshot */
    }
  }, [bootstrap, session, lkPatientId])

  useEffect(() => {
    if (!enabled || !lkPatientId) return
    void refreshSnapshot()
  }, [enabled, lkPatientId, refreshSnapshot])

  useEffect(() => {
    if (!enabled || !misPatientId || !lkPatientId || bootstrapSyncDone.current) return
    if (mergedSnapshot.findingsCount > 0) return
    bootstrapSyncDone.current = true
    void staffApi
      .syncDiagnocatPatient(bootstrap, session, misPatientId, lkPatientId)
      .then(() => refreshSnapshot())
      .catch(() => {})
  }, [enabled, misPatientId, lkPatientId, mergedSnapshot.findingsCount, bootstrap, session, refreshSnapshot])

  const clearStoredJob = useCallback(() => {
    if (misPatientId) {
      sessionStorage.removeItem(jobStorageKey(misPatientId))
    }
    setActiveJobId(null)
    setJob(null)
    setPollStartedAt(null)
    analysisReadyNotified.current = false
  }, [misPatientId])

  useEffect(() => {
    if (!analysisReady || !activeJobId) return
    clearStoredJob()
  }, [analysisReady, activeJobId, clearStoredJob])

  const submit = useCallback(
    async (studyUid: string) => {
      if (!misPatientId || !lkPatientId || !studyUid) return
      setIsSubmitting(true)
      try {
        const data = await staffApi.submitDiagnocatCt(bootstrap, session, {
          misPatientId,
          localPatientId: lkPatientId,
          studyUid,
        })
        const jobId = data.job?.id
        if (!jobId) throw new Error("job_id_missing")
        setActiveJobId(jobId)
        setPollStartedAt(Date.now())
        analysisReadyNotified.current = false
        sessionStorage.setItem(jobStorageKey(misPatientId), jobId)
      } finally {
        setIsSubmitting(false)
      }
    },
    [bootstrap, session, misPatientId, lkPatientId],
  )

  const resetJob = useCallback(() => {
    clearStoredJob()
  }, [clearStoredJob])

  useEffect(() => {
    if (!enabled || !activeJobId || !misPatientId) return
    let cancelled = false
    let timer: number | undefined

    const poll = async () => {
      if (cancelled) return
      if (pollStartedAt && Date.now() - pollStartedAt > MAX_POLL_MS) return

      setIsPolling(true)
      try {
        const data = await staffApi.getDiagnocatUploadJob(bootstrap, session, misPatientId, activeJobId)
        const nextJob = data.job ?? null
        if (cancelled) return
        setJob(nextJob)

        let nextSnapshot = mergedSnapshot
        if (lkPatientId) {
          try {
            const diag = await staffApi.getPatientDiagnocat(bootstrap, session, lkPatientId)
            nextSnapshot = snapshotFromDiagnocat(diag)
            if (!cancelled) setSnapshot(nextSnapshot)
          } catch {
            /* keep last snapshot */
          }
        }
        if (cancelled) return

        if (!shouldPollDiagnocatUploadJob(nextJob, nextSnapshot)) {
          setIsPolling(false)
          return
        }
        timer = window.setTimeout(() => void poll(), POLL_MS)
      } catch {
        if (!cancelled) {
          timer = window.setTimeout(() => void poll(), POLL_MS)
        }
      } finally {
        if (!cancelled && !timer) setIsPolling(false)
      }
    }

    void poll()
    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [enabled, activeJobId, misPatientId, lkPatientId, bootstrap, session, pollStartedAt, mergedSnapshot])

  useEffect(() => {
    if (!enabled || !needsSnapshotSync || !misPatientId || !lkPatientId) return

    const syncSnapshot = () => void refreshSnapshot()
    syncSnapshot()
    const snapshotTimer = window.setInterval(syncSnapshot, SYNC_MS)

    const syncPatient = () => {
      void staffApi.syncDiagnocatPatient(bootstrap, session, misPatientId, lkPatientId).catch(() => {})
    }
    syncPatient()
    const syncTimer = window.setInterval(syncPatient, SYNC_MS)

    return () => {
      window.clearInterval(snapshotTimer)
      window.clearInterval(syncTimer)
    }
  }, [enabled, needsSnapshotSync, misPatientId, lkPatientId, bootstrap, session, refreshSnapshot])

  useEffect(() => {
    if (!analysisReady || analysisReadyNotified.current) return
    analysisReadyNotified.current = true
    onAnalysisReady?.()
  }, [analysisReady, onAnalysisReady])

  const pollTimedOut = useMemo(
    () =>
      Boolean(pollStartedAt) &&
      Date.now() - (pollStartedAt ?? 0) > MAX_POLL_MS &&
      !analysisReady &&
      job?.status !== "failed",
    [pollStartedAt, analysisReady, job?.status],
  )

  return {
    job,
    snapshot: mergedSnapshot,
    analysisReady,
    jobPipelineActive,
    isSubmitting,
    submit,
    resetJob,
    isPolling,
    pollTimedOut,
  }
}
